import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Calendar, ChevronLeft, ChevronRight, X, MapPin, Clock, Users, AlertTriangle, Megaphone, ClipboardCheck } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Event type configurations
const EVENT_TYPES = {
  assemblee: {
    color: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    icon: Users,
    label: 'AG',
    path: '/assemblees'
  },
  evenement: {
    color: 'bg-purple-500',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200',
    icon: Calendar,
    label: 'Événement',
    path: '/evenements'
  },
  visite: {
    color: 'bg-amber-500',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200',
    icon: ClipboardCheck,
    label: 'Visite',
    path: '/visites'
  },
  incident: {
    color: 'bg-red-500',
    bgLight: 'bg-red-50',
    textColor: 'text-red-600',
    borderColor: 'border-red-200',
    icon: AlertTriangle,
    label: 'Incident',
    path: '/incidents-sinistres'
  },
  annonce: {
    color: 'bg-green-500',
    bgLight: 'bg-green-50',
    textColor: 'text-green-600',
    borderColor: 'border-green-200',
    icon: Megaphone,
    label: 'Annonce',
    path: '/annonces'
  }
};

const InteractiveCalendar = ({ compact = false }) => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch all events
  const fetchEvents = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const allEvents = [];
      
      // Fetch AGs
      try {
        const agRes = await axios.get(`${API}/assemblees`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        const ags = Array.isArray(agRes.data) ? agRes.data : [];
        ags.forEach(ag => {
          if (ag.date) {
            allEvents.push({
              id: ag.id,
              type: 'assemblee',
              title: ag.titre || 'Assemblée Générale',
              date: ag.date,
              time: ag.heure,
              location: ag.lieu,
              status: ag.status,
              building: ag.immeuble_nom
            });
          }
        });
      } catch (e) { console.log('No AG access'); }
      
      // Fetch Events
      try {
        const evtRes = await axios.get(`${API}/evenements`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        const evts = Array.isArray(evtRes.data) ? evtRes.data : [];
        evts.forEach(evt => {
          if (evt.date) {
            allEvents.push({
              id: evt.id,
              type: 'evenement',
              title: evt.titre || evt.title || 'Événement',
              date: evt.date,
              time: evt.heure || evt.time,
              location: evt.lieu || evt.location,
              description: evt.description
            });
          }
        });
      } catch (e) { console.log('No events access'); }
      
      // Fetch Visits (for gestionnaires)
      if (['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet'].includes(user?.role)) {
        try {
          const visitRes = await axios.get(`${API}/visites`, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
          const visits = Array.isArray(visitRes.data) ? visitRes.data : [];
          visits.forEach(visit => {
            if (visit.date) {
              allEvents.push({
                id: visit.id,
                type: 'visite',
                title: visit.titre || `Visite - ${visit.building_name || 'Immeuble'}`,
                date: visit.date,
                time: visit.heure,
                location: visit.building_name,
                status: visit.status
              });
            }
          });
        } catch (e) { console.log('No visits access'); }
      }
      
      // Fetch Incidents with dates (scheduled interventions)
      if (['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet', 'prestataire'].includes(user?.role)) {
        try {
          const incRes = await axios.get(`${API}/incidents-sinistres`, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
          const incidents = incRes.data?.incidents || [];
          incidents.forEach(inc => {
            if (inc.date_intervention) {
              allEvents.push({
                id: inc.id,
                type: 'incident',
                title: inc.titre || inc.title || 'Intervention',
                date: inc.date_intervention,
                time: inc.heure_intervention,
                location: inc.localisation,
                status: inc.status,
                urgency: inc.urgence
              });
            }
          });
        } catch (e) { console.log('No incidents access'); }
      }
      
      setEvents(allEvents);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      setLoading(false);
    }
  }, [token, user?.role]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Calendar helpers
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const today = new Date();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  // Get events for a specific day
  const getEventsForDay = (day) => {
    return events.filter(evt => {
      const evtDate = new Date(evt.date);
      return evtDate.getDate() === day && 
             evtDate.getMonth() === month && 
             evtDate.getFullYear() === year;
    });
  };

  // Handle day click
  const handleDayClick = (day) => {
    const dayEvents = getEventsForDay(day);
    if (dayEvents.length > 0) {
      setSelectedDate(new Date(year, month, day));
      setSelectedEvents(dayEvents);
      setShowModal(true);
    }
  };

  // Navigate to event
  const handleEventClick = (event) => {
    const config = EVENT_TYPES[event.type];
    if (config?.path) {
      navigate(`${config.path}?id=${event.id}`);
    }
    setShowModal(false);
  };

  // Format time
  const formatTime = (time) => {
    if (!time) return '';
    if (time.includes(':')) return time.substring(0, 5);
    return time;
  };

  // Render event dot
  const renderEventDots = (dayEvents) => {
    if (dayEvents.length === 0) return null;
    
    // Group by type and show max 3 dots
    const types = [...new Set(dayEvents.map(e => e.type))].slice(0, 3);
    
    return (
      <div className="flex justify-center gap-0.5 mt-0.5">
        {types.map((type, i) => (
          <div 
            key={i} 
            className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full ${EVENT_TYPES[type]?.color || 'bg-gray-400'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
      {/* Header */}
      <div className="p-4 md:p-5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button 
            onClick={prevMonth}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
          >
            <ChevronLeft size={18} className="text-slate-600" />
          </button>
          <h2 
            className="text-sm md:text-base font-semibold text-slate-800 capitalize min-w-[140px] text-center cursor-pointer hover:text-[#00D3A8]"
            onClick={goToToday}
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {monthName}
          </h2>
          <button 
            onClick={nextMonth}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
          >
            <ChevronRight size={18} className="text-slate-600" />
          </button>
        </div>
        {!compact && (
          <button
            onClick={goToToday}
            className="text-xs text-[#00D3A8] hover:underline"
          >
            Aujourd'hui
          </button>
        )}
      </div>
      
      {/* Calendar Grid */}
      <div className="p-3 md:p-4">
        <div className="grid grid-cols-7 gap-0.5 md:gap-1 text-center">
          {/* Headers */}
          {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, i) => (
            <div key={i} className="text-[10px] md:text-[11px] font-medium text-slate-400 p-1.5 md:p-2">
              {day}
            </div>
          ))}
          
          {/* Empty cells for offset */}
          {Array.from({ length: startOffset }, (_, i) => (
            <div key={`empty-${i}`} className="p-1.5 md:p-2"></div>
          ))}
          
          {/* Days */}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
            const isToday = day === today.getDate() && 
                           month === today.getMonth() && 
                           year === today.getFullYear();
            const dayEvents = getEventsForDay(day);
            const hasEvents = dayEvents.length > 0;
            
            return (
              <div
                key={day}
                onClick={() => handleDayClick(day)}
                className={`p-1 md:p-2 text-xs md:text-sm rounded-lg transition-all ${
                  hasEvents ? 'cursor-pointer' : ''
                } ${
                  isToday
                    ? 'bg-[#00D3A8] text-white font-semibold shadow-sm'
                    : hasEvents
                      ? 'hover:bg-slate-100 font-medium text-slate-700'
                      : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {day}
                {!isToday && renderEventDots(dayEvents)}
              </div>
            );
          })}
        </div>
        
        {/* Legend */}
        <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-slate-100">
          <div className="flex flex-wrap items-center gap-2 md:gap-3 text-[10px] md:text-[11px] text-slate-500">
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-[#00D3A8] rounded"></div>
              <span>Aujourd'hui</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-blue-500 rounded-full"></div>
              <span>AG</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-purple-500 rounded-full"></div>
              <span>Événement</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-amber-500 rounded-full"></div>
              <span>Visite</span>
            </div>
          </div>
        </div>
      </div>

      {/* Events Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="font-semibold text-slate-800">
                  {selectedDate?.toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long' 
                  })}
                </h3>
                <p className="text-xs text-slate-500">
                  {selectedEvents.length} événement{selectedEvents.length > 1 ? 's' : ''}
                </p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-slate-200 rounded-full transition-colors"
              >
                <X size={20} className="text-slate-500" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
              {selectedEvents.map((event, index) => {
                const config = EVENT_TYPES[event.type] || EVENT_TYPES.evenement;
                const Icon = config.icon;
                
                return (
                  <div
                    key={index}
                    onClick={() => handleEventClick(event)}
                    className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-all ${config.bgLight} ${config.borderColor}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${config.color} text-white`}>
                        <Icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${config.bgLight} ${config.textColor}`}>
                            {config.label}
                          </span>
                          {event.status && (
                            <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                              {event.status}
                            </span>
                          )}
                        </div>
                        <h4 className="font-medium text-slate-800 text-sm truncate">
                          {event.title}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                          {event.time && (
                            <div className="flex items-center gap-1">
                              <Clock size={12} />
                              <span>{formatTime(event.time)}</span>
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-1">
                              <MapPin size={12} />
                              <span className="truncate max-w-[150px]">{event.location}</span>
                            </div>
                          )}
                        </div>
                        {event.building && (
                          <p className="text-xs text-slate-400 mt-1 truncate">
                            {event.building}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Modal Footer */}
            <div className="p-3 border-t border-slate-100 bg-slate-50">
              <button
                onClick={() => setShowModal(false)}
                className="w-full py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveCalendar;
